import React, { useState, CSSProperties } from 'react';
import { StylishNewButton } from './StylishNewButton';
import Select, {
  ActionMeta,
  SingleValue,
  InputActionMeta,
  Props as ReactSelectProps,
} from 'react-select';
export interface ComboBoxOption {
  value: string;
  label: string;
}

export type StylishNewComboBoxProps = Pick<
  ReactSelectProps<ComboBoxOption>,
  | 'options'
  | 'defaultValue'
  | 'value'
  | 'onChange'
  | 'placeholder'
  | 'isClearable'
  | 'isDisabled'
  | 'filterOption'
> & {
  classes?: string;
  style?: CSSProperties;
  allowCustomValue?: boolean;
  customOptionLabel?: string;
  customInputMaxLength?: number;
};

export default function StylishNewComboBox({
  classes,
  style,
  options = [],
  defaultValue,
  value,
  onChange,
  placeholder,
  isClearable,
  isDisabled,
  filterOption,
  allowCustomValue = true,
  customOptionLabel = '+ Add Custom',
  customInputMaxLength,
  ...props
}: StylishNewComboBoxProps) {
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customInput, setCustomInput] = useState('');
  const [inlineInput, setInlineInput] = useState('');

  // Add "+ Add Custom" option at the top
  const customOption: ComboBoxOption = {
    value: '__custom__',
    label: customOptionLabel,
  };
  const enhancedOptions = allowCustomValue
    ? [customOption, ...options]
    : options;

  const handleChange = (
    newValue: SingleValue<ComboBoxOption>,
    actionMeta: ActionMeta<ComboBoxOption>
  ) => {
    if (newValue?.value === '__custom__') {
      setIsCustomMode(true);
      return;
    }

    if (onChange) {
      onChange(newValue, actionMeta);
    }
    setIsCustomMode(false);
    setInlineInput('');
  };

  const handleCustomSubmit = () => {
    if (customInput.trim()) {
      const newOption: ComboBoxOption = {
        value: customInput,
        label: customInput,
      };
      if (onChange) {
        onChange(newOption, {
          action: 'create-option',
          option: newOption,
        });
      }
      setCustomInput('');
      setIsCustomMode(false);
    }
  };

  const handleCustomCancel = () => {
    setCustomInput('');
    setIsCustomMode(false);
  };

  const handleInlineInputChange = (
    newValue: string,
    actionMeta: InputActionMeta
  ) => {
    if (actionMeta.action === 'input-change') {
      const next =
        customInputMaxLength !== undefined
          ? newValue.slice(0, customInputMaxLength)
          : newValue;
      setInlineInput(next);
    } else if (
      actionMeta.action === 'input-blur' ||
      actionMeta.action === 'menu-close'
    ) {
      setInlineInput('');
    }
  };

  const handleInlineSubmit = () => {
    const trimmed = inlineInput.trim();
    if (!trimmed) return;
    const newOption: ComboBoxOption = {
      value: trimmed,
      label: trimmed,
    };
    if (onChange) {
      onChange(newOption, {
        action: 'create-option',
        option: newOption,
      });
    }
    setInlineInput('');
  };

  const handleInlineCancel = () => {
    setInlineInput('');
  };

  const handleSelectKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && inlineInput.trim()) {
      e.preventDefault();
      handleInlineSubmit();
    }
  };

  const preserveFocusOnMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  if (isCustomMode) {
    return (
      <div
        className={'form-selectbox ' + (classes ? classes : '')}
        style={style}
      >
        <div className="d-flex gap-2">
          <input
            type="text"
            className="form-control"
            placeholder="Enter custom value..."
            value={customInput}
            maxLength={customInputMaxLength}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCustomSubmit();
              if (e.key === 'Escape') handleCustomCancel();
            }}
            autoFocus
          />
          <StylishNewButton
            className="me-2 button"
            onClick={handleCustomSubmit}
            disabled={!customInput.trim()}
          >
            Add
          </StylishNewButton>
          <StylishNewButton
            className="button--secondary button"
            onClick={handleCustomCancel}
          >
            Cancel
          </StylishNewButton>
        </div>
        {customInputMaxLength !== undefined && (
          <div className="form-text text-muted mt-1 text-start stylish-combobox-char-count">
            {customInput.length} / {customInputMaxLength}
          </div>
        )}
      </div>
    );
  }

  const showInlineActions = allowCustomValue && inlineInput.trim().length > 0;

  return (
    <div className={'form-selectbox ' + (classes ? classes : '')} style={style}>
      <div className="d-flex gap-2 align-items-start">
        <div className="flex-grow-1">
          <Select
            className="react-select-container"
            classNamePrefix="react-select"
            options={enhancedOptions}
            defaultValue={defaultValue}
            value={value}
            onChange={handleChange}
            onInputChange={handleInlineInputChange}
            onKeyDown={handleSelectKeyDown}
            placeholder={placeholder}
            isClearable={isClearable}
            isSearchable={true}
            filterOption={filterOption}
            isDisabled={isDisabled}
            noOptionsMessage={() => 'No Items Found'}
            {...props}
          />
        </div>
        {showInlineActions && (
          <>
            <StylishNewButton
              className="button"
              onMouseDown={preserveFocusOnMouseDown}
              onClick={handleInlineSubmit}
              disabled={!inlineInput.trim()}
            >
              Add
            </StylishNewButton>
            <StylishNewButton
              className="button--secondary button"
              onMouseDown={preserveFocusOnMouseDown}
              onClick={handleInlineCancel}
            >
              Cancel
            </StylishNewButton>
          </>
        )}
      </div>
      {showInlineActions && customInputMaxLength !== undefined && (
        <div className="form-text text-muted mt-1 text-start stylish-combobox-char-count">
          {inlineInput.length} / {customInputMaxLength}
        </div>
      )}
    </div>
  );
}
